import React from 'react';
import Typography from 'new/elements/Typography';
import { bytesToString } from 'js/lib/FormatUtils';
import { FileWithPreview } from './FileUploadConfiguration.types';
import { IconButton, Tooltip } from '@mui/material';
import {
  Close as CloseIcon,
  Visibility as VisibilityIcon,
  Block as BlockIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import { getFileMetadata } from './utils/fileUtils';
import styles from './FileUploadConfiguration.module.scss';

// ============================================
// Types
// ============================================

type FileListProps = {
  files: FileWithPreview[];
  onRemove: (fileId: string) => void;
  onPreview?: (file: FileWithPreview) => void;
  allowMultiple?: boolean;
};

// ============================================
// Helper Functions
// ============================================

function getStatusClassName(status: string): string {
  const capitalizedStatus = status.charAt(0).toUpperCase() + status.slice(1);
  return `${styles.fileStatus} ${styles[`fileStatus${capitalizedStatus}`]}`;
}

function getFileItemClassName(file: FileWithPreview): string {
  const baseClass = styles.fileItem;

  if (file.isBlocked) {
    return `${baseClass} ${styles.fileItemUnsupported}`;
  }
  if (file.isOverLimit) {
    return `${baseClass} ${styles.fileItemOverLimit}`;
  }

  switch (file.status) {
    case 'uploading':
      return `${baseClass} ${styles.fileItemUploading}`;
    case 'uploaded':
      return `${baseClass} ${styles.fileItemUploaded}`;
    case 'error':
      return `${baseClass} ${styles.fileItemError}`;
    default:
      return baseClass;
  }
}

function getExclusionLabel(file: FileWithPreview): string {
  if (file.isBlocked) return 'Unsupported';
  if (file.isOverLimit) return 'Over Limit';
  return '';
}

function getProgressValue(file: FileWithPreview): number {
  const upload = file.uploadProgress ?? 0;
  const backend = file.backendUploadProgress ?? 0;

  // Calculate combined progress: upload = 0-40%, backend = 40-100%
  const uploadContribution = Math.min(upload, 100) * 0.4;
  const backendContribution = Math.min(backend, 100) * 0.6;
  const total = Math.round(uploadContribution + backendContribution);

  // If uploading but no progress yet, show minimal indicator
  if (total === 0 && file.status === 'uploading') {
    return 3;
  }

  return Math.min(total, 100);
}

function shouldShowProgress(file: FileWithPreview): boolean {
  // Always show progress when status is uploading
  if (file.status === 'uploading') {
    return true;
  }

  // Show if there's active progress (not 0 and not 100)
  const upload = file.uploadProgress ?? 0;
  const backend = file.backendUploadProgress ?? 0;

  return (upload > 0 && upload < 100) || (backend > 0 && backend < 100);
}

// ============================================
// Main Component
// ============================================

export function FileList({ files, onRemove, onPreview, allowMultiple = true }: FileListProps) {
  const safeFiles = files || [];

  // Separate uploadable files from excluded files
  const uploadableFiles = safeFiles.filter((f) => !f.isBlocked && !f.isOverLimit);
  const excludedFiles = safeFiles.filter((f) => f.isBlocked || f.isOverLimit);

  const title = allowMultiple ? 'Selected Files' : 'Selected File';
  const uploadableCount = uploadableFiles.length;

  const renderFileItem = (file: FileWithPreview) => {
    const fileName = file.name || (file as any).displayName || 'Untitled';
    const fileSize = typeof file.size === 'number' ? file.size : (file as any).displaySize || 0;
    const fileType = file.type || (file as any).displayType || '';
    const metadata = getFileMetadata(fileName, fileType);
    const progress = getProgressValue(file);
    const showProgress = shouldShowProgress(file);
    const isComplete = file.status === 'uploaded';
    const isPending = file.status === 'pending' || !file.status;
    const isExcluded = file.isBlocked || file.isOverLimit;
    const exclusionLabel = getExclusionLabel(file);

    return (
      <div
        key={file.id}
        className={`${getFileItemClassName(file)} ${
          isPending && !isExcluded ? styles.fileItemCompact : ''
        }`}
      >
        <div className={styles.fileItemContent}>
          <div className={styles.fileIconWrapper}>
            {isExcluded ? (
              <Tooltip title={file.exclusionReason || 'File will not be uploaded'}>
                <span
                  className={file.isBlocked ? styles.fileIconUnsupported : styles.fileIconOverLimit}
                >
                  {file.isBlocked ? (
                    <BlockIcon fontSize="small" />
                  ) : (
                    <WarningIcon fontSize="small" />
                  )}
                </span>
              </Tooltip>
            ) : (
              <span className={styles.fileIcon}>{metadata.icon}</span>
            )}
          </div>

          {isPending && !isExcluded ? (
            <div className={styles.fileInfoCompact}>
              <Typography variant="bodyDefault" className={styles.fileNameCompact}>
                {fileName}
              </Typography>
              <div className={styles.fileMetaCompact}>
                <span className={styles.fileSize}>{bytesToString(fileSize)}</span>
                <span className={styles.fileType}>{metadata.label}</span>
                <span className={getStatusClassName('pending')}>Pending</span>
              </div>
            </div>
          ) : (
            <div className={styles.fileInfo}>
              <div className={styles.fileNameRow}>
                <Typography
                  variant="bodyDefault"
                  className={`${styles.fileName} ${isExcluded ? styles.fileNameExcluded : ''}`}
                >
                  {fileName}
                </Typography>
              </div>
              <div className={styles.fileMeta}>
                <span className={styles.fileSize}>{bytesToString(fileSize)}</span>
                <span className={styles.fileType}>{metadata.label}</span>
                {isExcluded ? (
                  <Tooltip title={file.exclusionReason || ''}>
                    <span
                      className={
                        file.isBlocked ? styles.fileStatusUnsupported : styles.fileStatusOverLimit
                      }
                    >
                      {exclusionLabel}
                    </span>
                  </Tooltip>
                ) : (
                  file.status && (
                    <span className={getStatusClassName(file.status)}>
                      {file.status === 'uploading' ? 'Uploading' : file.status}
                    </span>
                  )
                )}
              </div>

              {showProgress && !isExcluded && (
                <div className={styles.progressContainer}>
                  <div className={styles.progressBar}>
                    <div
                      className={`${styles.progressFill} ${
                        isComplete ? styles.progressFillComplete : ''
                      }`}
                      style={{ width: `${Math.max(progress, 3)}%` }}
                    />
                  </div>
                  <span className={styles.progressText}>{isComplete ? '✓' : `${progress}%`}</span>
                </div>
              )}
            </div>
          )}
        </div>

        <div className={styles.fileActions}>
          {onPreview && !isExcluded && (
            <IconButton
              size="small"
              onClick={() => onPreview(file)}
              className={styles.previewButton}
              title="Preview file"
            >
              <VisibilityIcon fontSize="small" />
            </IconButton>
          )}
          {allowMultiple && (
            <IconButton
              size="small"
              onClick={() => onRemove(file.id)}
              className={styles.removeButton}
              title="Remove file"
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          )}
        </div>
      </div>
    );
  };

  const blockedFiles = excludedFiles.filter((f) => f.isBlocked);
  const overLimitFiles = excludedFiles.filter((f) => f.isOverLimit && !f.isBlocked);

  return (
    <div className={styles.fileListContainer}>
      <div className={styles.fileListHeader}>
        <Typography variant="button2" className={styles.fileListTitle}>
          {title}
          {uploadableCount > 0 && (
            <span className={styles.fileCount}>{uploadableCount} to upload</span>
          )}
        </Typography>
      </div>

      {uploadableFiles.length > 0 && (
        <div className={styles.fileList}>{uploadableFiles.map(renderFileItem)}</div>
      )}

      {blockedFiles.length > 0 && (
        <>
          <div className={styles.unsupportedFilesHeader}>
            <BlockIcon
              fontSize="inherit"
              style={{ marginRight: 'var(--spacing-sm)', verticalAlign: 'middle' }}
            />
            <Typography variant="caption" className={styles.unsupportedFilesTitle}>
              {blockedFiles.length} unsupported file{blockedFiles.length > 1 ? 's' : ''} will not be
              uploaded
            </Typography>
          </div>
          <div className={styles.fileList}>{blockedFiles.map(renderFileItem)}</div>
        </>
      )}

      {overLimitFiles.length > 0 && (
        <>
          <div className={styles.excludedFilesHeader}>
            <WarningIcon
              fontSize="inherit"
              style={{ marginRight: 'var(--spacing-sm)', verticalAlign: 'middle' }}
            />
            <Typography variant="caption" className={styles.excludedFilesTitle}>
              {overLimitFiles.length} file{overLimitFiles.length > 1 ? 's' : ''} exceed
              {overLimitFiles.length === 1 ? 's' : ''} the upload limit
            </Typography>
          </div>
          <div className={styles.fileList}>{overLimitFiles.map(renderFileItem)}</div>
        </>
      )}
    </div>
  );
}
